import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-location-field>` says about itself.
 *
 * ONE VALUE, TWO BOXES. A Geolocation value is a single point with two named coordinates, and the
 * kernel never lets either be written on its own. So this is one control: the two boxes commit
 * together when focus leaves the whole control or Enter is pressed, and moving from one box to the
 * other commits nothing.
 *
 * IT CHECKS, BUT IT IS NOT THE CHECK. Before committing it applies the save's own Geolocation rules —
 * a missing or non-numeric coordinate, a latitude outside -90 to 90, a longitude outside -180 to 180
 * — and shows the save's wording under the box that is wrong. The save still judges every write;
 * this only means the person hears about it while the box is still in front of them.
 *
 * THE PAIR IS PLACED AS TWO NUMBERS. A placement writes attributes, so the stored pair goes in as
 * `latitude` and `longitude`. `value` — the `{ latitude, longitude }` object, or `null` — is a
 * property, readable after a `change` and settable from script, and is documented as state rather
 * than as an input for that reason.
 */
const docs: ComponentDocs = {
  aliases: ['geolocation field', 'coordinates input', 'latitude and longitude', 'map point input', 'place input'],

  answers: [
    'ask for a place as a latitude and a longitude',
    'edit a Geolocation field',
    'refuse a coordinate that is not on Earth before it is saved',
    'take a coordinate pair pasted from a map',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description: "The field's name, drawn above the two boxes and naming the group they form. Left unset, no label is drawn.",
    },
    {
      name: 'latitude',
      attribute: 'latitude',
      type: 'number',
      description:
        'The latitude of the place already set, shown in the first box. Written back here whenever a pair is committed, ' +
        'and removed when the place is cleared.',
    },
    {
      name: 'longitude',
      attribute: 'longitude',
      type: 'number',
      description:
        'The longitude of the place already set, shown in the second box. Written back here whenever a pair is committed, ' +
        'and removed when the place is cleared.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description:
        'This field has to be filled in. Draws the asterisk and marks both boxes required. Leaving it empty is answered by ' +
        'the save, as it is for every other field.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not type in this. Greys both boxes and refuses focus.',
      default: false,
    },
    {
      name: 'readonly',
      attribute: 'readonly',
      type: 'boolean',
      description:
        'You may read this, select it and copy it, but not change it. Unlike `disabled`, both boxes stay in full colour ' +
        'and can be focused; nothing typed or pasted is committed.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description:
        'The place as a whole is wrong. Turns both boxes and the field message red. For a reason that belongs to one ' +
        'coordinate, use that coordinate’s message instead.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description: 'The line under both boxes — muted helper text on its own, the red reason when `invalid` is also set.',
    },
    {
      name: 'latitudeMessage',
      attribute: 'latitude-message',
      type: 'text',
      description:
        'A reason the page has for refusing the latitude, shown in red under that box. A reason the control finds itself ' +
        'is shown ahead of it.',
    },
    {
      name: 'longitudeMessage',
      attribute: 'longitude-message',
      type: 'text',
      description:
        'A reason the page has for refusing the longitude, shown in red under that box. A reason the control finds itself ' +
        'is shown ahead of it.',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant: two underlined boxes in the surrounding type, each with its Latitude or Longitude ' +
        'label beside it. The group label and the field message are hidden; the box labels and a refusal under a box are not.',
      default: false,
    },
  ],

  events: [
    {
      name: 'change',
      when:
        'A different pair, or no place, is committed — focus leaves the whole control, or Enter is pressed. Never when focus ' +
        'moves between the two boxes, never for a pair that is refused, and never for the pair already committed.',
      carries: 'nothing; read `value` for the pair',
    },
  ],

  state: [
    {
      name: 'value',
      type: '{ latitude: number; longitude: number } | null',
      description:
        'The pair in the boxes, or `null` when both are empty. While the boxes hold a refused pair it is the last pair ' +
        'committed. It can also be set from script.',
    },
    {
      name: 'holdsRefusal',
      type: 'boolean',
      description:
        'Whether a refusal the control found is showing under either box, so the pair is held rather than committed. A ' +
        'host that closes the control when focus leaves reads this to keep it open instead of dropping what was typed.',
    },
  ],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the labels, the boxes and the messages.' },
    { token: '--caos-text-body', controls: 'The size of the group label and of the typed coordinates.' },
    { token: '--caos-text-label', controls: 'The size of the Latitude and Longitude labels.' },
    { token: '--caos-space-1', controls: 'The gap between a label, its box and its message.' },
    { token: '--caos-space-3', controls: 'The gap between the two boxes, and the vertical padding inside each.' },
    { token: '--caos-radius', controls: 'The corner radius of the boxes.' },
    { token: '--caos-color-border', controls: 'The box outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The box outline while it has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the boxes.' },
    { token: '--caos-color-text', controls: 'The colour of the typed coordinates.' },
    { token: '--caos-color-text-muted', controls: 'The labels, the placeholder, the helper message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills a box while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, a refused box, and every refusal message.' },
  ],

  parts: [
    { part: 'label', is: 'the group label above both boxes' },
    { part: 'control', is: 'the row holding the two boxes' },
    { part: 'latitude', is: 'the latitude box' },
    { part: 'longitude', is: 'the longitude box' },
    { part: 'latitude-message', is: 'the refusal under the latitude box' },
    { part: 'longitude-message', is: 'the refusal under the longitude box' },
    { part: 'message', is: 'the line under both boxes' },
  ],

  methods: [
    {
      name: 'focus',
      description: 'Put the caret in the latitude box, with what is there selected.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section, wide enough for two boxes side by side. The boxes split the width evenly and ' +
    'never wrap onto two lines, so a narrow column makes both narrow rather than stacking them.',

  whenToUse:
    'A place the person types or pastes as coordinates, where the value is a Geolocation field or will be saved as one. ' +
    'Reach for it over two NumberFields whenever the two numbers are one place: that is what makes them commit together, ' +
    'and what keeps an empty box from being saved as zero.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws this piece for a Geolocation field.',
    },
    { name: 'NumberField', when: 'The number stands on its own — an elevation, a radius, a distance.' },
    { name: 'TextField', when: 'The place is an address a person reads, not a point on a map.' },
  ],

  accessibility:
    'The two boxes sit in a group named by the label, and each is a real text input with its own label, so each is ' +
    'announced as "Latitude" or "Longitude" within the field. They open the decimal keypad on a touch device. A refusal is ' +
    'tied to its box with `aria-describedby` and marks that box `aria-invalid`, so it is read with the box it is about. The box ' +
    'labels stay on screen in the inline variant as well: a record page opens it already holding a place, where a placeholder ' +
    'would never be seen.',

  examples: [
    {
      key: 'set',
      label: 'A place already set',
      summary: 'A site with its coordinates filled in — the two numbers that are one place, side by side under one label.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site', latitude: 29.7604, longitude: -95.3698 },
      },
    },
    {
      key: 'refused',
      label: 'A coordinate refused',
      summary:
        'The same field holding a latitude that is not on Earth: the refusal sits under the latitude box alone, and the ' +
        'longitude that is fine stays unmarked.',
      placement: {
        component: 'caos-location-field',
        inputs: {
          label: 'Site',
          latitude: 129.7604,
          longitude: -95.3698,
          latitudeMessage: 'Latitude 129.7604 is outside the range -90 to 90, so it is not a point on Earth.',
        },
      },
    },
    {
      key: 'required',
      label: 'Required and empty',
      summary: 'An empty field that has to be filled in — the asterisk on the group label, and both boxes marked required.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site', required: true },
      },
    },
    {
      key: 'basic',
      label: 'Empty, ready for a place',
      summary: 'The field with nothing set yet: a Latitude box and a Longitude box under one label, both empty, which means no place.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site' },
      },
    },
    {
      key: 'disabled',
      label: 'Disabled',
      summary: 'A place that may not be changed here at all: both boxes greyed, and neither takes focus.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site', latitude: 29.7604, longitude: -95.3698, disabled: true },
      },
    },
    {
      key: 'readonly',
      label: 'Read-only',
      summary:
        'A place a person may read and copy but not change. The boxes keep their colour and take focus, so the numbers ' +
        'can be selected — only a dashed edge says they take no typing.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site', latitude: 29.7604, longitude: -95.3698, readonly: true },
      },
    },
    {
      key: 'help',
      label: 'With help text',
      summary: 'A line under both boxes saying what the place is for, and that a pasted "latitude, longitude" fills the two at once.',
      placement: {
        component: 'caos-location-field',
        inputs: {
          label: 'Site',
          message: 'Where the crew reports on day one. Paste "latitude, longitude" from any map to fill both boxes.',
        },
      },
    },
    {
      key: 'rtl',
      label: 'Right to left',
      summary:
        'The same field in a right-to-left page. The boxes swap sides with the writing direction; latitude is still the ' +
        'first box in reading order, so it is the one on the right.',
      placement: {
        component: 'caos-location-field',
        inputs: { label: 'Site', latitude: 29.7604, longitude: -95.3698 },
      },
      host: {
        summary: 'sets `dir="rtl"` on the field, as a right-to-left page would on its root.',
        run: (stage) => {
          stage.querySelectorAll('caos-location-field').forEach((node) => node.setAttribute('dir', 'rtl'));
        },
      },
    },
  ],
};

export default docs;
